import React, { useState } from 'react';
import { Sparkles, ArrowRight, RotateCcw, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { ListeningRuleEditable } from './ListeningRuleDialog';
import type { FenceEditable } from './FenceDialog';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AIGeneratedRule {
  rule_type: 'filter' | 'listening'
  name: string
  keywords: string[]
  blocked_hashtags: string[]
  platforms: string[]
  intensity_gauge: number
  rationale: string
}

interface AIRuleBuilderProps {
  onCreateListening: (value: ListeningRuleEditable) => void
  onCreateFence: (value: FenceEditable) => void
}

// ── Component ─────────────────────────────────────────────────────────────────

export const AIRuleBuilder: React.FC<AIRuleBuilderProps> = ({
  onCreateListening,
  onCreateFence,
}) => {
  const [open, setOpen] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const [rule, setRule] = useState<AIGeneratedRule | null>(null)

  const generate = async () => {
    if (!prompt.trim()) return
    setLoading(true)
    setRule(null)
    try {
      const { data, error } = await supabase.functions.invoke('ai-assistant', {
        body: { action: 'rule_builder', prompt: prompt.trim() },
      })
      if (error) throw error
      if (!data?.success) throw new Error(data?.error ?? 'AI returned an error')
      setRule(data.rule as AIGeneratedRule)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not generate rule')
    } finally {
      setLoading(false)
    }
  }

  const accept = () => {
    if (!rule) return
    if (rule.rule_type === 'filter') {
      onCreateFence({
        name: rule.name,
        keywords: rule.keywords ?? [],
        blocked_users: [],
        blocked_hashtags: rule.blocked_hashtags ?? [],
        blocked_accounts: [],
        fence_new_accounts: false,
        new_account_threshold_days: 30,
        fence_low_followers: false,
        min_follower_threshold: null,
        platforms: rule.platforms ?? [],
        rss_feeds: [],
        intensity_gauge: rule.intensity_gauge ?? 3,
      })
    } else {
      onCreateListening({
        name: rule.name,
        keywords: rule.keywords ?? [],
        platforms: rule.platforms ?? [],
        rss_feeds: [],
        intensity_gauge: rule.intensity_gauge ?? 3,
        highlight_style: { badge: true },
      })
    }
    reset()
  }

  const reset = () => {
    setRule(null)
    setPrompt('')
  }

  return (
    <div className="rounded-lg border border-[#1A2E4A]/15 bg-[#1A2E4A]/[0.02]">
      {/* Header toggle */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#1A2E4A]/60" />
          <span className="text-sm font-medium">Describe a rule in plain language</span>
          <Badge variant="secondary" className="text-[10px] py-0 px-1.5">AI</Badge>
        </div>
        {open
          ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
          : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          <p className="text-xs text-muted-foreground">
            Write what you want the rule to do — AI will convert it into keywords, platforms, and settings.
          </p>

          {/* Examples */}
          <div className="flex flex-wrap gap-1.5">
            {[
              'Hide promotional crypto posts on Reddit',
              'Surface AI and machine learning news from all sources',
              'Block aggressive political arguments on Bluesky',
            ].map((ex) => (
              <button
                key={ex}
                onClick={() => setPrompt(ex)}
                className="text-[10px] px-2 py-0.5 rounded-full border border-border/60 text-muted-foreground hover:bg-muted transition-colors"
              >
                {ex}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="flex gap-2">
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. Hide posts about cryptocurrency price predictions on Reddit…"
              className="min-h-[64px] text-sm resize-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) generate()
              }}
            />
            <Button
              size="sm"
              onClick={generate}
              disabled={loading || !prompt.trim()}
              className="shrink-0 bg-[#1A2E4A] hover:bg-[#1A2E4A]/90 text-white self-end"
            >
              {loading ? (
                <RotateCcw className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ArrowRight className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>

          {/* Generated rule preview */}
          {rule && (
            <Card className="border-[#1A2E4A]/20 bg-background">
              <CardContent className="p-3 space-y-2.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{rule.name}</span>
                    <Badge
                      variant={rule.rule_type === 'filter' ? 'destructive' : 'default'}
                      className="text-[10px] py-0 px-1.5"
                    >
                      {rule.rule_type === 'filter' ? 'Fence' : 'Listen'}
                    </Badge>
                  </div>
                  <span className="text-[10px] text-muted-foreground">
                    Intensity {rule.intensity_gauge ?? 3}/5
                  </span>
                </div>

                {rule.rationale && (
                  <p className="text-xs text-muted-foreground italic">{rule.rationale}</p>
                )}

                {rule.keywords?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    <span className="text-[10px] text-muted-foreground font-medium mr-1">Keywords:</span>
                    {rule.keywords.map((kw) => (
                      <Badge key={kw} variant="secondary" className="text-[10px] py-0 px-1.5">
                        {kw}
                      </Badge>
                    ))}
                  </div>
                )}

                {rule.blocked_hashtags?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    <span className="text-[10px] text-muted-foreground font-medium mr-1">Hashtags:</span>
                    {rule.blocked_hashtags.map((ht) => (
                      <Badge key={ht} variant="secondary" className="text-[10px] py-0 px-1.5">
                        #{ht}
                      </Badge>
                    ))}
                  </div>
                )}

                {rule.platforms?.length > 0 && (
                  <p className="text-[10px] text-muted-foreground">
                    <span className="font-medium">Platforms: </span>
                    {rule.platforms.join(', ')}
                  </p>
                )}

                <div className="flex items-center gap-2 pt-1">
                  <Button size="sm" onClick={accept} className="bg-[#1A2E4A] hover:bg-[#1A2E4A]/90 text-white h-7 text-xs">
                    <Check className="h-3 w-3 mr-1" />
                    Create this rule
                  </Button>
                  <Button size="sm" variant="ghost" onClick={reset} className="h-7 text-xs">
                    Try again
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  )
}

export default AIRuleBuilder
